/**
 * Floating HUD for active measure / draw tools while Simple mode hides the side panels.
 */
import React, { useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PlaceIcon from '@mui/icons-material/Place';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setMeasurementMode,
  clearMeasurement,
  type MeasurementMode,
} from '../store/slices/measurementSlice';
import { setActiveTool as setDrawingTool } from '../store/slices/drawingSlice';
import { formatDistance, formatArea } from '../services/MeasurementService';
import { useGeoveraTheme } from '../theme/ThemeContext';

const SimpleModeToolHud: React.FC = () => {
  const dispatch = useAppDispatch();
  const { palette } = useGeoveraTheme();
  const {
    activeMode,
    currentDistance,
    currentArea,
    distanceUnits,
    areaUnits,
    isMeasuring,
    distancePoints,
    areaPoints,
  } = useAppSelector((s) => s.measurement);
  const drawingTool = useAppSelector((s) => s.drawing.activeTool);
  const isSimple = useAppSelector((s) => s.ui.isNonTechnicalMode);

  const showMeasure = isSimple && activeMode != null;
  const showDraw = isSimple && drawingTool != null && !showMeasure;

  const stopMeasure = useCallback(() => {
    dispatch(clearMeasurement());
    dispatch(setMeasurementMode(null));
  }, [dispatch]);

  const stopDraw = useCallback(() => {
    dispatch(setDrawingTool(null));
  }, [dispatch]);

  const handleMode = useCallback(
    (_: React.MouseEvent<HTMLElement>, mode: MeasurementMode) => {
      if (mode) dispatch(setMeasurementMode(mode));
    },
    [dispatch]
  );

  if (!showMeasure && !showDraw) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: 16,
        transform: 'translateX(-50%)',
        zIndex: 45,
        px: 1.5,
        py: 1.25,
        minWidth: { xs: 'min(340px, calc(100% - 24px))', sm: 380 },
        maxWidth: 'calc(100% - 24px)',
        bgcolor: alpha(palette.bg.elevated, 0.97),
        border: `1px solid ${palette.border.base}`,
        borderRadius: 2,
        backdropFilter: 'blur(12px)',
      }}
    >
      {showMeasure && (
        <Stack spacing={1}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Chip size="small" color="warning" label="Measuring" sx={{ fontWeight: 700 }} />
            <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '0.9rem' }}>
              Click the map to add points · double-click to finish
            </Typography>
            <IconButton size="small" onClick={stopMeasure} aria-label="Stop measuring">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={activeMode}
            onChange={handleMode}
            sx={{ alignSelf: 'flex-start' }}
          >
            <ToggleButton value="distance" sx={{ textTransform: 'none', gap: 0.5, px: 1.5 }}>
              <StraightenIcon sx={{ fontSize: 18 }} /> Distance
            </ToggleButton>
            <ToggleButton value="area" sx={{ textTransform: 'none', gap: 0.5, px: 1.5 }}>
              <SquareFootIcon sx={{ fontSize: 18 }} /> Area
            </ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: alpha(palette.accent.primary || '#4fc3f7', 0.08),
              border: `1px solid ${palette.border.subtle}`,
            }}
          >
            {activeMode === 'distance' && (
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                {isMeasuring || distancePoints.length > 0
                  ? formatDistance(currentDistance, distanceUnits)
                  : 'Click two or more points…'}
              </Typography>
            )}
            {activeMode === 'area' && (
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                {isMeasuring || areaPoints.length > 0
                  ? formatArea(currentArea, areaUnits)
                  : 'Click around the shape…'}
              </Typography>
            )}
            {activeMode === 'coordinate' && (
              <Typography sx={{ fontWeight: 700 }}>Click the map for coordinates</Typography>
            )}
          </Box>

          <Button
            size="small"
            onClick={stopMeasure}
            sx={{ textTransform: 'none', alignSelf: 'flex-end' }}
          >
            Done measuring
          </Button>
        </Stack>
      )}

      {showDraw && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Chip
            size="small"
            color="success"
            icon={drawingTool === 'point' ? <PlaceIcon /> : <EditIcon />}
            label={drawingTool === 'point' ? 'Add points' : 'Draw boundary'}
            sx={{ fontWeight: 700 }}
          />
          <Typography sx={{ flex: 1, fontSize: '0.85rem', color: palette.text.secondary }}>
            {drawingTool === 'point'
              ? 'Click the map to drop pins'
              : 'Click corners · double-click to close the shape'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={stopDraw}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Done
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default SimpleModeToolHud;
